import { ReactNode, forwardRef, useEffect, useState } from "react";
import { NavLink, useLocation, matchPath } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import NotificationBell from "@/components/NotificationBell";
import { ChatMessageNotifier } from "@/components/chat/ChatMessageNotifier";
import BrandLogo from "@/components/BrandLogo";
import GlobalSearch from "@/components/GlobalSearch";
import { useTheme } from "next-themes";
import {
  CalendarDays,
  BarChart3,
  ClipboardList,
  Users,
  MessageSquare,
  BriefcaseBusiness,
  FileText,
  Package,
  Stethoscope,
  Settings,
  CircleHelp,
  Folder,
  FolderOpen,
  Sun,
  Moon,
  Shield,
  User,
  LogOut,
  Dumbbell,
  DollarSign,
  type LucideIcon,
} from "lucide-react";

type NavItem = {to: string;label: string;icon: LucideIcon;masterOnly?: boolean;group?: "main" | "admin" | "support";};

const navItems: NavItem[] = [
{ to: "/", label: "Agenda", icon: CalendarDays, group: "main" },
{ to: "/pacientes", label: "Pacientes", icon: Users, group: "main" },
{ to: "/prontuario", label: "Prontuário", icon: ClipboardList, group: "main" },
{ to: "/laudos", label: "Laudos", icon: FileText, group: "main" },
{ to: "/chat", label: "Chat", icon: MessageSquare, group: "main" },
{ to: "/tabela", label: "Tabela de Preços", icon: DollarSign, masterOnly: true, group: "main" },
{ to: "/receituario", label: "Receituário", icon: Dumbbell, group: "main" },
  { to: "/dashboard", label: "Dashboard", icon: BarChart3, masterOnly: true, group: "main" },
{ to: "/financeiro", label: "Financeiro", icon: BriefcaseBusiness, masterOnly: true, group: "admin" },
{ to: "/registro", label: "Registro", icon: ClipboardList, masterOnly: true, group: "admin" },
{ to: "/estoque", label: "Estoque", icon: Package, masterOnly: true, group: "admin" },
{ to: "/profissionais", label: "Profissionais", icon: Stethoscope, masterOnly: true, group: "admin" },
{ to: "/configuracoes", label: "Configurações", icon: Settings, masterOnly: true, group: "support" },
{ to: "/ajuda", label: "Ajuda", icon: CircleHelp, group: "support" }];


const AppLayout = forwardRef<HTMLDivElement, { children: ReactNode }>(function AppLayout({ children }, ref) {
  const { signOut, user, profile, professional, isMaster } = useAuth();
  useOnlineStatus({ observeOnlineUsers: false });
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const [mounted, setMounted] = useState(false);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(() => localStorage.getItem("nitfisio-menu-collapsed") === "true");
  const [isMobileLandscape, setIsMobileLandscape] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px) and (orientation: landscape)").matches;
  });
  const [isMobilePortrait, setIsMobilePortrait] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 1024px) and (orientation: portrait) and (pointer: coarse)").matches;
  });

  const allItems = navItems.filter((item) => !item.masterOnly || isMaster);

  useEffect(() => {
    localStorage.setItem("nitfisio-menu-collapsed", String(isMenuCollapsed));
  }, [isMenuCollapsed]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px) and (orientation: landscape)");

    const updateMobileLandscape = () => {
      setIsMobileLandscape(mediaQuery.matches);
    };

    updateMobileLandscape();
    mediaQuery.addEventListener("change", updateMobileLandscape);

    return () => mediaQuery.removeEventListener("change", updateMobileLandscape);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1024px) and (orientation: portrait) and (pointer: coarse)");

    const updateMobilePortrait = () => {
      setIsMobilePortrait(mediaQuery.matches);
    };

    updateMobilePortrait();
    mediaQuery.addEventListener("change", updateMobilePortrait);

    return () => mediaQuery.removeEventListener("change", updateMobilePortrait);
  }, []);

  const roleColor = professional?.user_type === "master" || isMaster ? "hsl(var(--destructive))" : "hsl(var(--info))";
  const emailPrefix = user?.email?.split("@")[0]?.trim();
  const metadataName =
  typeof user?.user_metadata?.full_name === "string" ?
  user.user_metadata.full_name.trim().split(/\s+/)[0] :
  typeof user?.user_metadata?.name === "string" ?
  user.user_metadata.name.trim().split(/\s+/)[0] :
  undefined;
  const shortName =
  professional?.name?.trim().split(/\s+/)[0] ||
  profile?.full_name?.trim().split(/\s+/)[0] ||
  metadataName;
  const displayName = professional?.nickname?.trim() || shortName || emailPrefix;

  const isNavItemActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return Boolean(matchPath({ path: `${path}/*` }, location.pathname) || location.pathname === path);
  };

  return (
    <div
      ref={ref}
      className={cn(
        "relative min-h-screen flex flex-col bg-background/70 text-foreground overflow-x-hidden md:h-[100dvh] md:overflow-hidden",
        isMobilePortrait && "h-[100dvh] min-h-[100dvh] overflow-hidden"
      )}
    >
      <ChatMessageNotifier />
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="animate-aurora absolute -top-48 left-[-12rem] h-96 w-96 rounded-full bg-primary/25 blur-3xl" />
        <div className="animate-aurora absolute right-[-10rem] top-28 h-[26rem] w-[26rem] rounded-full bg-violet-400/20 blur-3xl [animation-delay:1.8s]" />
        <div className="animate-aurora absolute bottom-[-12rem] left-1/3 h-[22rem] w-[22rem] rounded-full bg-fuchsia-300/20 blur-3xl [animation-delay:3.2s]" />
      </div>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground">
        
        Pular para o conteúdo principal
      </a>
      {/* Top bar */}
      <header
        className={cn(
          "z-50 mx-2 mt-[max(0.5rem,env(safe-area-inset-top))] flex items-center justify-between rounded-2xl border border-white/40 bg-card/75 px-4 py-3 shadow-lg shadow-primary/10 backdrop-blur-xl supports-[backdrop-filter]:bg-card/55 md:mx-4 md:fixed md:left-0 md:right-0 md:top-0",
          isMobilePortrait ? "fixed left-0 right-0 top-0" : "sticky top-0"
        )}
      >
        <div className="flex items-center gap-3">
          {!isMobileLandscape && !isMobilePortrait &&
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMenuCollapsed((previous) => !previous)}
            title={isMenuCollapsed ? "Expandir menu" : "Recolher menu"}
            className="shrink-0">
            
              {isMenuCollapsed ? <FolderOpen className="h-4 w-4" /> : <Folder className="h-4 w-4" />}
            </Button>
          }
          <BrandLogo className="w-9 h-9 rounded-xl bg-transparent p-0" imageClassName="rounded-xl" />
          <div>
            <h1 className="text-lg font-bold tracking-tight leading-none">SERAFISIO</h1>
            <p className="text-xs text-muted-foreground">by Nitfisio<sup>®</sup></p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <GlobalSearch />
          <Button variant="outline"
          size="sm"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          className="gap-2"
          title="Alternar tema"
          disabled={!mounted}>
            
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            <span className="hidden sm:inline">{theme === "dark" ? "Tema claro" : "Tema escuro"}</span>
          </Button>
          {user && displayName &&
          <div className="hidden items-center gap-2 rounded-lg border bg-background px-3 py-1.5 sm:flex">
              <span className="text-sm font-semibold" style={{ color: roleColor }}>
                {displayName}
              </span>
              <span className={cn(
              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
              isMaster ?
              "bg-destructive/10 text-destructive" :
              "bg-info/10 text-info"
            )}>
                {isMaster ? <Shield className="h-3 w-3" /> : <User className="h-3 w-3" />}
                {isMaster ? "Master" : "Standard"}
              </span>
            </div>
          }
          <NotificationBell />
          <Button variant="ghost" size="icon" onClick={signOut} title="Sair">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main
        id="main-content"
        className={cn(
          "flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y transition-[padding] duration-300 md:pb-4 animate-slide-in",
          isMobilePortrait ? "pt-[calc(env(safe-area-inset-top)+6.75rem)] pb-[calc(env(safe-area-inset-bottom)+5.75rem)]" : "pt-3 pb-24 md:pt-24",
          isMenuCollapsed ? "md:pl-24" : "md:pl-60"
        )}
      >
        <div className="mx-2 max-w-[1600px] xl:mx-auto rounded-2xl border border-white/30 bg-card/65 p-2 shadow-xl shadow-primary/5 backdrop-blur md:mx-4 md:p-3">
          {children}
        </div>
      </main>

      {/* Bottom navigation (mobile) */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-white/25 bg-card/65 backdrop-blur-xl pb-[env(safe-area-inset-bottom)] md:hidden">
        <div className="overflow-x-auto overflow-y-hidden overscroll-x-contain px-2 py-3 touch-pan-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="mx-auto flex w-max min-w-full snap-x snap-mandatory items-center justify-start gap-1 whitespace-nowrap">
            {allItems.map((item) => {
              const active = isNavItemActive(item.to);
              const ItemIcon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex shrink-0 snap-start flex-col items-center gap-1 rounded-lg px-3 py-1.5 text-xs transition-colors",
                    active ? "text-primary" : "text-muted-foreground"
                  )}>
                  
                  <ItemIcon className={cn("h-5 w-5 transition-transform duration-200", active && "scale-110")} />
                  <span className="font-medium">{item.label}</span>
                  {active &&
                  <span className="w-1.5 h-1.5 rounded-full bg-primary -mt-0.5" />
                  }
                </NavLink>);

            })}
          </div>
        </div>
      </nav>

      {/* Desktop sidebar nav */}
      <nav
        className={cn(
          "hidden md:flex fixed left-0 top-0 bottom-0 border-r border-white/25 flex-col pt-20 z-40 overflow-hidden transition-all duration-300",
          isMenuCollapsed ? "w-24" : "w-60"
        )}
        style={{
          background: "linear-gradient(180deg, hsl(var(--card) / 0.74) 0%, hsl(var(--card) / 0.52) 100%)",
          backdropFilter: "blur(18px)",
          boxShadow: "0 24px 60px -30px hsl(var(--primary) / 0.45)"
        }}>
        
        <div className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
          {allItems.map((item, index) => {
            const active = isNavItemActive(item.to);
            const ItemIcon = item.icon;
            const prevItem = allItems[index - 1];
            const showSeparator = index > 0 && prevItem && prevItem.group !== item.group;
            return (
              <div key={item.to}>
                {showSeparator &&
                <div className="my-2 mx-2 border-t border-border/40" />
                }
                <NavLink
                  to={item.to}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex items-center px-3 py-2.5 rounded-lg transition-all duration-200 text-sm font-medium",
                    isMenuCollapsed ? "justify-center" : "gap-3",
                    active ?
                    "bg-primary text-primary-foreground shadow-md scale-[1.02]" :
                    "text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:scale-[1.01]"
                  )}
                  title={isMenuCollapsed ? item.label : undefined}>
                  
                  <ItemIcon className="h-4 w-4" />
                  {!isMenuCollapsed && item.label}
                </NavLink>
              </div>);

          })}
        </div>
      </nav>
    </div>);

});

export default AppLayout;
